/*
 * Copyright (C) 2026 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { inject, Injectable } from '@angular/core';
import { Resource, ResourceUri } from '@ngxp/rest';
import { isUndefined, upperFirst } from 'lodash-es';
import { Observable } from 'rxjs';
import { AdditionalFeatureKey, FeatureInfo } from '../ngrx/state.model';
import { StateService } from '../ngrx/state.service';
import { ListResourceService } from '../resource/list-resource.service';
import { ListItemResource, ListResourceServiceConfig, ResourceServiceConfig } from '../resource/resource.model';
import { ResourceService } from '../resource/resource.service';
import { ListResource, StateResource } from '../resource/resource.util';
import { isNotUndefined } from '../tech.util';

@Injectable()
export abstract class BasedService<
  B extends Resource,
  T extends Resource,
  LB extends Resource,
  LT extends ListResource,
  LI extends ListItemResource,
  S extends ResourceService<B, T>,
  LS extends ListResourceService<LB, LT, LI>,
> {
  private readonly stateService: StateService = inject(StateService);

  _resourceService: S;
  _listResourceService: LS;

  _featureInfo: FeatureInfo;

  constructor() {
    this._init();
  }

  _init(): void {
    this._featureInfo = this._buildFeatureInfo();

    this._initServices();

    this.stateService.registerFeature(this._featureInfo);
  }

  _buildFeatureInfo(): FeatureInfo {
    const listResourcePath: string = `${this.getFeatureName().toLowerCase()}List`;
    return {
      stateName: `${upperFirst(this.getFeatureName())}State`,
      resourcePath: this.getFeatureName().toLowerCase(),
      listResourcePath,
      additionalFeature: this.getAdditionalFeatures(listResourcePath),
    };
  }

  abstract getFeatureName(): string;

  getAdditionalFeatures(listResourcePath: string): AdditionalFeatureKey[] {
    return [];
  }

  _initServices(): void {
    if (isNotUndefined(this.getListResourceConfig())) {
      this._listResourceService = this._createListResourceService(this.buildListResourceStateInfo());
    }
    if (isNotUndefined(this.getResourceConfig())) {
      this._resourceService = this._createResourceService(this.buildResourceStateInfo());
    }
  }

  abstract getResourceConfig(): ResourceServiceConfig<B>;

  private buildResourceStateInfo(): ResourceServiceConfig<B> {
    return {
      ...this.getResourceConfig(),
      stateInfo: { name: this._featureInfo.stateName, path: this._featureInfo.resourcePath },
    };
  }

  abstract _createResourceService(config: ResourceServiceConfig<B>): S;

  abstract getListResourceConfig(): ListResourceServiceConfig<LB>;

  private buildListResourceStateInfo(): ListResourceServiceConfig<LB> {
    return {
      ...this.getListResourceConfig(),
      stateInfo: { name: this._featureInfo.stateName, path: this._featureInfo.listResourcePath },
    };
  }

  abstract _createListResourceService(config: ListResourceServiceConfig<LB>): LS;

  public get(): Observable<StateResource<T>> {
    return this._getResourceService().get();
  }

  public selectResource(): Observable<StateResource<T>> {
    return this._getResourceService().selectResource();
  }

  public clearResource(): void {
    this._getResourceService().clearResource();
  }

  _getResourceService(): S {
    if (isUndefined(this._resourceService)) {
      throw new Error(`Resource service for '${this.getFeatureName()}' is not configured.`);
    }
    return this._resourceService;
  }

  public getList(compareFn: (toCompare: LI, compareWith: LI) => number = () => 0): Observable<StateResource<LT>> {
    return this._getListResourceService().getList(compareFn);
  }

  public clearList(): void {
    return this._getListResourceService().clearList();
  }

  public markListAsReload(): void {
    return this._getListResourceService().refresh();
  }

  public isListEmpty(): Observable<boolean> {
    return this._getListResourceService().isListEmpty();
  }

  _getListResourceService(): LS {
    if (isUndefined(this._listResourceService)) {
      throw new Error(`Listresource service for '${this.getFeatureName()}' is not configured.`);
    }
    return this._listResourceService;
  }

  public select(uri: ResourceUri): void {
    this._getListResourceService().select(uri);
  }

  public getSelected(): Observable<StateResource<LI>> {
    return this._getListResourceService().getSelected();
  }
}
